import React from 'react';
import { Icon, Spin, Drawer, Modal, FG, Pagination } from '../components/ui.jsx';
import { api, fmt, fmtD, showToast } from '../api.js';

const STATUS_LABELS = {
  pending:     {label:'Da importare',  bg:'#FEF3C7', color:'#92400E', border:'#FDE68A'},
  imported:    {label:'Importato',     bg:'#DBEAFE', color:'#1D4ED8', border:'#93C5FD'},
  transformed: {label:'Fatturato',     bg:'#DCFCE7', color:'#166534', border:'#86EFAC'},
  ignored:     {label:'Ignorato',      bg:'#F3F4F6', color:'#6B7280', border:'#E5E7EB'},
};

const StatusPill = ({status}) => {
  const c = STATUS_LABELS[status] || STATUS_LABELS.pending;
  return <span style={{display:'inline-block',padding:'2px 9px',borderRadius:20,fontSize:11,
    fontWeight:600,background:c.bg,color:c.color,border:`1px solid ${c.border}`}}>{c.label}</span>;
};

export const QuotesPage = ({onNav}) => {
  const [quotes, setQuotes] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);
  const [filterStatus, setFilterStatus] = React.useState('pending');
  const [sel, setSel] = React.useState(null);
  const [importing, setImporting] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const PAGE = 20;

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams({limit:PAGE, offset});
    if (filterStatus) qs.set('status', filterStatus);
    api(`/integrations/scan360/quotes?${qs}`)
      .then(d => { setQuotes(d.items||[]); setTotal(d.total||0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, [filterStatus, offset]);
  React.useEffect(() => { setOffset(0); }, [filterStatus]);

  React.useEffect(() => {
    api('/integrations/scan360/status').then(setStatus).catch(() => setStatus({configured:false}));
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api('/integrations/scan360/sync-quotes', 'POST');
      const l = r.local || {};
      showToast(`Sync OK: ${l.new||0} nuovi, ${l.updated||0} aggiornati`, 'success');
      load();
    } catch(e) {}
    finally { setSyncing(false); }
  };

  const importAs = async (q) => {
    if (!confirm(`Importare "${q.subject || q.quote_number}" come cantiere?`)) return;
    setImporting(q.id);
    try {
      const r = await api(`/integrations/scan360/quotes/${q.id}/import-as-project`, 'POST');
      showToast(`Cantiere ${r.project_code} creato`, 'success');
      load();
      if (onNav) onNav('projects', r.project_id);
    } catch(e) {}
    finally { setImporting(null); }
  };

  const openDetail = (q) => setSel(q);
  const goToProject = (pid) => { if (onNav && pid) onNav('projects', pid); };

  if (status && !status.configured) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Preventivi</div>
        </div>
        <div className="card" style={{padding:30,textAlign:'center'}}>
          <Icon name="alert-circle" size={32} color="var(--n300)" style={{marginBottom:12,display:'block',margin:'0 auto'}}/>
          <div style={{fontSize:14,fontWeight:600,color:'var(--n700)',marginBottom:6}}>
            Integrazione Fatture in Cloud non configurata
          </div>
          <div style={{fontSize:12,color:'var(--n400)',marginBottom:16}}>
            {typeof status.error === 'string' ? status.error : 'Vai in Impostazioni per configurare SCAN360.'}
          </div>
          <button className="btn btn-primary" onClick={() => onNav && onNav('settings')}>
            <Icon name="settings" size={13} color="#fff"/>Vai alle impostazioni
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Preventivi</div>
          <div className="page-title-sub">{total} preventivi{filterStatus ? ` · ${STATUS_LABELS[filterStatus]?.label||filterStatus}` : ''}</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <select className="input" style={{width:170}} value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Tutti</option>
            <option value="pending">Da importare</option>
            <option value="imported">Importati</option>
            <option value="transformed">Fatturati</option>
            <option value="ignored">Ignorati</option>
          </select>
          <button className="btn btn-primary" onClick={syncNow} disabled={syncing || !status?.ok}>
            {syncing ? <Spin/> : <Icon name="download-cloud" size={13} color="#fff"/>}
            Sincronizza da FiC
          </button>
        </div>
      </div>

      {status && !status.ok && (
        <div className="card" style={{padding:'10px 14px',marginBottom:12,
          background:'#FEF3C7',borderColor:'#FDE68A',display:'flex',alignItems:'center',gap:8}}>
          <Icon name="alert-triangle" size={14} color="#92400E"/>
          <span style={{fontSize:12,color:'#92400E'}}>
            Connessione SCAN360 non attiva. Verifica configurazione in Impostazioni.
          </span>
        </div>
      )}

      <div className="card" style={{padding:0}}>
        {loading ? <div className="empty" style={{padding:40}}><Spin/></div> : (
          <table>
            <thead>
              <tr>
                <th>N°</th><th>Data</th><th>Cliente</th><th>Oggetto</th>
                <th style={{textAlign:'right'}}>Totale</th><th>Stato</th><th></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.id} style={{cursor:'pointer'}} onClick={() => openDetail(q)}>
                  <td style={{fontWeight:600,fontSize:12}}>{q.quote_number || '—'}</td>
                  <td style={{fontSize:12,color:'var(--n500)'}}>{fmtD(q.quote_date)}</td>
                  <td style={{fontSize:12}}>
                    <div style={{fontWeight:600}}>{q.contact_name || '—'}</div>
                    {q.contact_tax_id && <div style={{fontSize:10,color:'var(--n400)'}}>{q.contact_tax_id}</div>}
                  </td>
                  <td style={{fontSize:12,maxWidth:300,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {q.subject || q.visible_subject || '—'}
                  </td>
                  <td style={{fontWeight:600,textAlign:'right',color:'#B87D00'}}>
                    {fmt(q.total_amount)}
                  </td>
                  <td><StatusPill status={q.status}/></td>
                  <td style={{textAlign:'right',whiteSpace:'nowrap'}}>
                    {q.project_id && (
                      <button className="btn btn-ghost btn-sm" title="Apri cantiere"
                        style={{padding:'4px 8px',fontSize:11}}
                        onClick={e => { e.stopPropagation(); goToProject(q.project_id); }}>
                        <Icon name="external-link" size={11} color="var(--blue)"/>
                        {q.project_code || `#${q.project_id}`}
                      </button>
                    )}
                    {!q.project_id && q.status === 'pending' && (
                      <button className="btn btn-primary btn-sm" disabled={importing===q.id}
                        style={{fontSize:11}}
                        onClick={e => { e.stopPropagation(); importAs(q); }}>
                        {importing===q.id ? <Spin/> : <Icon name="plus" size={11} color="#fff"/>}
                        Crea cantiere
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!quotes.length && (
                <tr><td colSpan={7} className="empty" style={{padding:30}}>
                  <Icon name="file-text" size={28} color="var(--n300)"
                    style={{marginBottom:8,display:'block',margin:'0 auto 8px'}}/>
                  {filterStatus === 'pending'
                    ? 'Nessun preventivo da importare. Clicca "Sincronizza da FiC" per aggiornare.'
                    : 'Nessun preventivo in questa categoria.'}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
        <div style={{padding:'0 16px'}}>
          <Pagination total={total} limit={PAGE} offset={offset} onChange={setOffset}/>
        </div>
      </div>

      {/* Drawer dettaglio */}
      <Drawer open={!!sel} onClose={() => setSel(null)}
        title={sel?.subject || sel?.quote_number} subtitle={`Preventivo FiC`} width={460}>
        {sel && (
          <div>
            <div style={{padding:'12px 16px',background:'var(--n50)',borderRadius:10,marginBottom:16}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <span style={{fontSize:12,color:'var(--n400)',textTransform:'uppercase',letterSpacing:'.05em',fontWeight:700}}>
                  {sel.quote_number || 'Senza numero'}
                </span>
                <StatusPill status={sel.status}/>
              </div>
              <div style={{fontSize:20,fontWeight:700,color:'#B87D00'}}>{fmt(sel.total_amount)}</div>
              <div style={{fontSize:11,color:'var(--n400)',marginTop:4}}>
                {sel.taxable_amount && `Imponibile ${fmt(sel.taxable_amount)}`}
                {sel.tax_amount && ` · IVA ${fmt(sel.tax_amount)}`}
              </div>
            </div>

            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:'var(--n400)',textTransform:'uppercase',letterSpacing:'.05em',fontWeight:700,marginBottom:6}}>
                Cliente
              </div>
              <div style={{fontSize:14,fontWeight:600}}>{sel.contact_name || '—'}</div>
              {sel.contact_tax_id && <div style={{fontSize:11,color:'var(--n400)'}}>P.IVA/CF: {sel.contact_tax_id}</div>}
            </div>

            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:'var(--n400)',textTransform:'uppercase',letterSpacing:'.05em',fontWeight:700,marginBottom:6}}>
                Date
              </div>
              <div style={{fontSize:12}}>
                Emissione: <strong>{fmtD(sel.quote_date)}</strong>
                {sel.expiry_date && <> · Scadenza: <strong>{fmtD(sel.expiry_date)}</strong></>}
              </div>
            </div>

            {(sel.subject || sel.visible_subject) && (
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,color:'var(--n400)',textTransform:'uppercase',letterSpacing:'.05em',fontWeight:700,marginBottom:6}}>
                  Oggetto
                </div>
                <div style={{fontSize:13,color:'var(--n700)',lineHeight:1.5}}>
                  {sel.subject || sel.visible_subject}
                </div>
              </div>
            )}

            {sel.project_id && (
              <div style={{padding:'10px 14px',background:'#DBEAFE',borderRadius:8,marginBottom:14,
                display:'flex',alignItems:'center',gap:10}}>
                <Icon name="check-circle" size={16} color="#1D4ED8"/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#1E40AF'}}>Importato come cantiere</div>
                  <div style={{fontSize:11,color:'#1D4ED8'}}>{sel.project_code}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => { setSel(null); goToProject(sel.project_id); }}>
                  <Icon name="external-link" size={11} color="var(--blue)"/>Apri
                </button>
              </div>
            )}

            <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:20,paddingTop:14,borderTop:'1px solid var(--n100)'}}>
              {sel.fic_url && (
                <a href={sel.fic_url} target="_blank" rel="noopener" className="btn btn-ghost btn-sm"
                  style={{textDecoration:'none'}}>
                  <Icon name="external-link" size={13}/>Vedi PDF in FiC
                </a>
              )}
              {!sel.project_id && sel.status === 'pending' && (
                <button className="btn btn-primary btn-sm" onClick={() => importAs(sel)}
                  disabled={importing===sel.id}>
                  {importing===sel.id ? <Spin/> : <Icon name="plus" size={13} color="#fff"/>}
                  Crea cantiere
                </button>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};
